import React, { useState, useEffect, useCallback } from 'react';
import { RaceEvent, CrewMember, Document } from '../types';
import { eventService } from '../services/eventService';
import { crewService } from '../services/crewService';
import { documentService } from '../services/documentService';
import EventDropdown from '../components/EventDropdown';
import CrewTable from '../components/CrewTable';
import AddCrewModal from '../components/AddCrewModal';

/**
 * Build Crew List Page — consolidated event selection and crew management.
 *
 * Requirements:
 *  1.1-1.5 — Event selection dropdown
 *  2.1-2.7 — Crew table with certificate status
 *  3.1-3.6 — Add crew functionality
 *  4.1-4.4 — Remove crew functionality
 *  5.1-5.3 — Certificate validation
 */

const EventSelectionPage: React.FC = () => {
  // Events state
  const [events, setEvents] = useState<RaceEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState<string | null>(null);
  
  // Selected event state
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  
  // Crew state
  const [eventCrew, setEventCrew] = useState<CrewMember[]>([]);
  const [masterCrew, setMasterCrew] = useState<CrewMember[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [crewLoading, setCrewLoading] = useState(false);
  const [crewError, setCrewError] = useState<string | null>(null);
  
  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  
  // Operation state
  const [addingCrewId, setAddingCrewId] = useState<string | null>(null);
  const [removingCrewId, setRemovingCrewId] = useState<string | null>(null);
  const [invitingCrewId, setInvitingCrewId] = useState<string | null>(null);
  const [operationError, setOperationError] = useState<string | null>(null);

  // Fetch events on mount
  const fetchEvents = useCallback(async () => {
    setEventsLoading(true);
    setEventsError(null);
    try {
      const data = await eventService.getEvents();
      setEvents(data);
    } catch {
      setEventsError('Failed to load race events. Please try again.');
    } finally {
      setEventsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Fetch master crew list and documents on mount
  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const [crewData, docsData] = await Promise.all([
          crewService.getCrew(),
          documentService.getDocuments(),
        ]);
        setMasterCrew(crewData);
        setDocuments(docsData);
      } catch {
        // Silent fail - will show empty available crew in modal
      }
    };
    fetchMasterData();
  }, []);

  // Fetch event crew when event is selected
  const fetchEventCrew = useCallback(async (raceId: string) => {
    setCrewLoading(true);
    setCrewError(null);
    try {
      const data = await eventService.getEventCrew(raceId);
      setEventCrew(data);
    } catch {
      setCrewError('Failed to load crew for this event.');
    } finally {
      setCrewLoading(false);
    }
  }, []);

  const handleSelectEvent = (eventId: string) => {
    setSelectedEventId(eventId);
    setOperationError(null);
    fetchEventCrew(eventId);
  };

  // Add crew to event (supports multiple)
  const handleAddCrew = async (crewIds: string[]) => {
    if (!selectedEventId) return;
    
    setAddingCrewId(crewIds[0]);
    setOperationError(null);
    try {
      for (const crewId of crewIds) {
        setAddingCrewId(crewId);
        await eventService.addCrewToEvent(selectedEventId, { crewId });
      }
      await fetchEventCrew(selectedEventId);
      setShowAddModal(false);
    } catch {
      setOperationError('Failed to add crew member(s). Please try again.');
    } finally {
      setAddingCrewId(null);
    }
  };

  // Remove crew from event
  const handleRemoveCrew = async (crewId: string) => {
    if (!selectedEventId) return;
    
    setRemovingCrewId(crewId);
    setOperationError(null);
    try {
      await eventService.removeCrewFromEvent(selectedEventId, crewId);
      // Update local state
      setEventCrew(prev => prev.filter(c => c.crewId !== crewId));
    } catch {
      setOperationError('Failed to remove crew member. Please try again.');
    } finally {
      setRemovingCrewId(null);
    }
  };

  // Send WhatsApp invite to crew member
  const handleInviteCrew = async (crewId: string) => {
    if (!selectedEventId) return;

    setInvitingCrewId(crewId);
    setOperationError(null);
    try {
      await eventService.sendInvite(selectedEventId, crewId);
      await fetchEventCrew(selectedEventId);
    } catch {
      setOperationError('Failed to send invite. Check WhatsApp settings.');
    } finally {
      setInvitingCrewId(null);
    }
  };

  // Calculate available crew (master crew not in event)
  const eventCrewIds = new Set(eventCrew.map(c => c.crewId));
  const availableCrew = masterCrew.filter(c => !eventCrewIds.has(c.crewId));

  // Get selected event name for display
  const selectedEvent = events.find(e => e.raceId === selectedEventId);

  return (
    <div className="build-crew-page">
      <div className="page-header">
        <h1>Build Crew List</h1>
        <p className="page-description">
          Select a race event and manage your crew list.
        </p>
      </div>

      {eventsError && (
        <div className="error-banner">
          <p>{eventsError}</p>
          <button onClick={fetchEvents} className="btn-retry">Retry</button>
        </div>
      )}

      <EventDropdown
        events={events}
        selectedEventId={selectedEventId}
        onSelectEvent={handleSelectEvent}
        loading={eventsLoading}
      />

      {selectedEventId && (
        <div className="crew-section">
          <div className="crew-section-header">
            <h2>Crew for {selectedEvent?.name || 'Selected Event'}</h2>
            <button
              className="primary-button"
              onClick={() => setShowAddModal(true)}
            >
              Add Crew
            </button>
          </div>

          {crewError && (
            <div className="error-banner">
              <p>{crewError}</p>
              <button onClick={() => fetchEventCrew(selectedEventId)} className="btn-retry">
                Retry
              </button>
            </div>
          )}

          {operationError && (
            <div className="error-message">{operationError}</div>
          )}

          {crewLoading ? (
            <div className="loading-state">
              <p>Loading crew...</p>
            </div>
          ) : (
            <CrewTable
              crew={eventCrew}
              documents={documents}
              raceId={selectedEventId}
              onRemoveCrew={handleRemoveCrew}
              onInviteCrew={handleInviteCrew}
              removingCrewId={removingCrewId}
              invitingCrewId={invitingCrewId}
            />
          )}
        </div>
      )}

      {!selectedEventId && !eventsLoading && events.length > 0 && (
        <div className="select-event-prompt">
          <p>Select an event above to manage its crew list.</p>
        </div>
      )}

      <AddCrewModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        availableCrew={availableCrew}
        documents={documents}
        onAddCrew={handleAddCrew}
        adding={addingCrewId !== null}
      />
    </div>
  );
};

export default EventSelectionPage;
